import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { formatDate } from '../lib/utils';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import {
  Copy, ExternalLink, Upload, Settings, ShieldCheck, Loader2, Plus, Trash2, AlertTriangle, CheckCircle, Info
} from 'lucide-react';
import { toast } from 'sonner';

const CHANNELS = {
  meta: { label: 'Meta', className: 'bg-blue-50 text-blue-700 border-blue-200' },
  google: { label: 'Google', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  custom: { label: 'Vlastní', className: 'bg-slate-100 text-slate-700 border-slate-200' },
};

const SEVERITY = {
  error: { label: 'chyba', className: 'bg-red-50 text-red-700 border-red-200', icon: AlertTriangle },
  warning: { label: 'varování', className: 'bg-amber-50 text-amber-800 border-amber-200', icon: AlertTriangle },
  info: { label: 'info', className: 'bg-blue-50 text-blue-700 border-blue-200', icon: Info },
};

const selectCls = 'flex h-9 w-full rounded-md border border-input bg-white px-2 text-sm';

const FILTER_FIELDS = ['availability', 'is_active', 'brand', 'product_type', 'label_field', 'label_value', 'price_min', 'price_max', 'has_overlay'];

function cleanFilters(filters) {
  const out = {};
  FILTER_FIELDS.forEach(key => {
    const value = filters?.[key];
    if (value !== '' && value !== null && value !== undefined) out[key] = value;
  });
  if (!out.label_field) delete out.label_value;
  return out;
}

function OutputSettingsDialog({ projectId, output, open, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [options, setOptions] = useState(null);
  const [matching, setMatching] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!output) return;
    setForm({
      name: output.name,
      variant_mode: output.variant_mode,
      use_overlays: output.use_overlays,
      title_max_length: output.title_max_length || '',
      is_enabled: output.is_enabled,
      filters: { ...Object.fromEntries(FILTER_FIELDS.map(k => [k, ''])), ...(output.filters || {}) },
    });
  }, [output]);

  useEffect(() => {
    if (!open) return;
    api.get(`/projects/${projectId}/products/filter-options`).then(res => setOptions(res.data)).catch(() => {});
  }, [open, projectId]);

  // Kolik produktů filtr kanálu propustí (před pravidly)
  useEffect(() => {
    if (!form) return;
    const timer = setTimeout(() => {
      const params = new URLSearchParams({ limit: '1', ...cleanFilters(form.filters) });
      api.get(`/projects/${projectId}/products?${params}`).then(res => setMatching(res.data.total)).catch(() => setMatching(null));
    }, 300);
    return () => clearTimeout(timer);
  }, [form, projectId]);

  if (!form) return null;
  const setFilter = (key, value) => setForm(f => ({ ...f, filters: { ...f.filters, [key]: value } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/projects/${projectId}/outputs/${output.id}`, {
        name: form.name,
        variant_mode: form.variant_mode,
        use_overlays: form.use_overlays,
        title_max_length: Number(form.title_max_length) || 0,
        filters: cleanFilters(form.filters),
        ...(output.is_primary ? {} : { is_enabled: form.is_enabled }),
      });
      toast.success('Kanál uložen');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Uložení se nezdařilo');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Nastavení kanálu</DialogTitle>
          <DialogDescription>Co do kanálu půjde a v jaké podobě. Změny se projeví při další publikaci.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1">
            <Label>Název</Label>
            <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>Varianty</Label>
              <select value={form.variant_mode} onChange={e => setForm(f => ({ ...f, variant_mode: e.target.value }))} className={selectCls} data-testid="output-variant-mode">
                <option value="all_variants">každá varianta zvlášť (doporučeno)</option>
                <option value="merged">sloučit varianty do jedné položky</option>
              </select>
            </div>
            <div className="space-y-1">
              <Label>Max. délka názvu</Label>
              <Input type="number" min="0" value={form.title_max_length} placeholder="bez limitu"
                onChange={e => setForm(f => ({ ...f, title_max_length: e.target.value }))} />
            </div>
          </div>

          <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
            <div>
              <div className="text-sm font-medium">Obrázky s overlayem</div>
              <p className="text-xs text-slate-500">Google promo grafiku v obrázcích zakazuje — u Google kanálu nech vypnuté.</p>
            </div>
            <Switch checked={form.use_overlays} onCheckedChange={v => setForm(f => ({ ...f, use_overlays: v }))} />
          </div>

          {!output.is_primary && (
            <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
              <div className="text-sm font-medium">Kanál zapnutý (publikuje se)</div>
              <Switch checked={form.is_enabled} onCheckedChange={v => setForm(f => ({ ...f, is_enabled: v }))} />
            </div>
          )}

          <div className="rounded-lg border border-slate-200 p-3 space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium">Které produkty do kanálu půjdou</div>
              {matching !== null && <Badge className="bg-slate-100 text-slate-700 border-slate-200">{matching} produktů</Badge>}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <select value={form.filters.availability} onChange={e => setFilter('availability', e.target.value)} className={selectCls}>
                <option value="">dostupnost: vše</option>
                <option value="in stock">jen skladem</option>
                <option value="out of stock">jen vyprodané</option>
                <option value="preorder">jen předobjednávky</option>
              </select>
              <select value={form.filters.brand} onChange={e => setFilter('brand', e.target.value)} className={selectCls}>
                <option value="">značka: vše</option>
                {options?.brands.map(b => <option key={b.value} value={b.value}>{b.value} ({b.count})</option>)}
              </select>
              <select value={form.filters.product_type} onChange={e => setFilter('product_type', e.target.value)} className={selectCls}>
                <option value="">kategorie: vše</option>
                {options?.product_types.map(t => <option key={t.value} value={t.value}>{t.value.length > 55 ? `…${t.value.slice(-53)}` : t.value} ({t.count})</option>)}
              </select>
              <div className="flex gap-2">
                <select value={form.filters.label_field} onChange={e => setForm(f => ({ ...f, filters: { ...f.filters, label_field: e.target.value, label_value: '' } }))} className={selectCls}>
                  <option value="">label: —</option>
                  {[0, 1, 2, 3, 4].map(i => <option key={i} value={`custom_label_${i}`}>label {i}</option>)}
                </select>
                <select value={form.filters.label_value} onChange={e => setFilter('label_value', e.target.value)} className={selectCls} disabled={!form.filters.label_field}>
                  <option value="">vyplněný</option>
                  <option value="__empty__">prázdný</option>
                  {(options?.labels?.[form.filters.label_field] || []).map(v => <option key={v.value} value={v.value}>{v.value}</option>)}
                </select>
              </div>
              <div className="flex gap-2">
                <Input type="number" min="0" placeholder="cena od" value={form.filters.price_min} onChange={e => setFilter('price_min', e.target.value)} className="h-9" />
                <Input type="number" min="0" placeholder="cena do" value={form.filters.price_max} onChange={e => setFilter('price_max', e.target.value)} className="h-9" />
              </div>
            </div>
            <p className="text-xs text-slate-500">Počet je před pravidly. Pravidla jde omezit na tento kanál v jejich nastavení.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Zrušit</Button>
          <Button className="bg-amber-500 hover:bg-amber-600 text-slate-900" onClick={save} disabled={saving} data-testid="output-save">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Uložit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function OutputsPanel({ projectId, onPublished }) {
  const [outputs, setOutputs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [editing, setEditing] = useState(null);
  const [validation, setValidation] = useState(null);
  const [validating, setValidating] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const fetchOutputs = useCallback(async () => {
    try {
      const res = await api.get(`/projects/${projectId}/outputs`);
      setOutputs(res.data);
    } catch (err) {
      toast.error('Nepodařilo se načíst výstupy');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchOutputs(); }, [fetchOutputs]);

  const copy = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('URL zkopírována');
  };

  const addOutput = async (channel) => {
    setShowAdd(false);
    try {
      const res = await api.post(`/projects/${projectId}/outputs`, { channel });
      toast.success(`Přidán kanál ${res.data.name} — publikuj ho, ať vznikne URL`);
      await fetchOutputs();
      setEditing(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kanál se nepodařilo přidat');
    }
  };

  const publish = async (output) => {
    setBusy(output.id);
    try {
      const res = await api.post(`/projects/${projectId}/outputs/${output.id}/publish`);
      const result = res.data.outputs?.[0];
      toast.success(`${output.name}: v${result?.version}, ${result?.product_count} položek`);
      fetchOutputs();
      onPublished?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Publikace selhala');
    } finally {
      setBusy(null);
    }
  };

  const validate = async (output) => {
    setValidating(output.id);
    try {
      const res = await api.get(`/projects/${projectId}/outputs/${output.id}/validate`);
      setValidation({ output, ...res.data });
    } catch (err) {
      toast.error('Kontrola se nepodařila');
    } finally {
      setValidating(null);
    }
  };

  const remove = async (output) => {
    if (!window.confirm(`Smazat kanál ${output.name}? Jeho URL přestane fungovat.`)) return;
    try {
      await api.delete(`/projects/${projectId}/outputs/${output.id}`);
      toast.success('Kanál smazán');
      fetchOutputs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Smazání selhalo');
    }
  };

  return (
    <Card className="border border-slate-200" data-testid="outputs-panel">
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle className="text-lg font-heading">Kanály</CardTitle>
          <CardDescription>
            Každý kanál má vlastní stabilní URL feedu, výběr produktů a může mít vlastní pravidla.
          </CardDescription>
        </div>
        <div className="relative">
          <Button variant="outline" onClick={() => setShowAdd(!showAdd)} data-testid="add-output-btn">
            <Plus className="w-4 h-4 mr-2" /> Přidat kanál
          </Button>
          {showAdd && (
            <div className="absolute right-0 mt-1 w-64 rounded-md border border-slate-200 bg-white shadow-lg z-10 p-1">
              {[
                ['google', 'Google Merchant Center', 'bez overlayů, názvy do 150 znaků'],
                ['meta', 'Meta (Facebook a Instagram)', 'overlaye povolené'],
              ].map(([channel, name, hint]) => (
                <button key={channel} onClick={() => addOutput(channel)} className="w-full text-left px-3 py-2 rounded hover:bg-amber-50" data-testid={`add-output-${channel}`}>
                  <div className="text-sm font-medium">{name}</div>
                  <div className="text-xs text-slate-500">{hint}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-amber-500" /></div>
        ) : outputs.map(output => {
          const channel = CHANNELS[output.channel] || CHANNELS.custom;
          return (
            <div key={output.id} className={`rounded-lg border p-3 space-y-2 ${output.is_enabled ? 'border-slate-200' : 'border-slate-200 opacity-60'}`} data-testid={`output-${output.channel}`}>
              <div className="flex flex-wrap items-center gap-2">
                <Badge className={channel.className}>{channel.label}</Badge>
                <span className="font-medium text-slate-900">{output.name}</span>
                {output.is_primary && <Badge variant="outline" className="text-xs">hlavní</Badge>}
                {!output.is_enabled && <Badge variant="outline" className="text-xs">vypnutý</Badge>}
                <span className="text-xs text-slate-500">
                  {output.variant_mode === 'merged' ? 'sloučené varianty · ' : ''}
                  {output.use_overlays ? 's overlayi' : 'bez overlayů'}
                  {output.title_max_length ? ` · názvy do ${output.title_max_length}` : ''}
                  {Object.keys(output.filters || {}).length > 0 ? ' · s filtrem' : ''}
                </span>
                <div className="ml-auto flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => validate(output)} disabled={validating === output.id} data-testid={`validate-${output.channel}`}>
                    {validating === output.id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-1" />} Kontrola
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEditing(output)} data-testid={`settings-${output.channel}`}>
                    <Settings className="w-4 h-4" />
                  </Button>
                  <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-slate-900" onClick={() => publish(output)} disabled={busy === output.id} data-testid={`publish-${output.channel}`}>
                    {busy === output.id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} Publikovat
                  </Button>
                  {!output.is_primary && (
                    <Button size="sm" variant="ghost" className="text-slate-400 hover:text-red-600" onClick={() => remove(output)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Input value={output.url} readOnly className="font-mono text-xs h-8 bg-slate-50" />
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => copy(output.url)} title="Kopírovat URL"><Copy className="w-4 h-4" /></Button>
                <Button asChild variant="outline" size="icon" className="h-8 w-8" title="Otevřít">
                  <a href={output.url} target="_blank" rel="noopener noreferrer"><ExternalLink className="w-4 h-4" /></a>
                </Button>
              </div>
              <div className="text-xs text-slate-500">
                {output.last_publish
                  ? <>Publikováno {formatDate(output.last_publish)}{output.item_count != null ? ` · ${output.item_count} položek` : ''}</>
                  : 'Zatím nepublikováno — URL začne fungovat po první publikaci.'}
                {output.last_publish_error && <span className="text-red-600 ml-2">Chyba: {output.last_publish_error}</span>}
              </div>
            </div>
          );
        })}
      </CardContent>

      {editing && (
        <OutputSettingsDialog projectId={projectId} output={editing} open={!!editing}
          onClose={() => setEditing(null)} onSaved={fetchOutputs} />
      )}

      <Dialog open={!!validation} onOpenChange={o => { if (!o) setValidation(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Kontrola kanálu {validation?.output?.name}</DialogTitle>
            <DialogDescription>
              Kontroluje se přesně to XML, které by se teď publikovalo — podle pravidel kanálu {CHANNELS[validation?.channel]?.label}.
            </DialogDescription>
          </DialogHeader>
          {validation && (
            <div className="space-y-3">
              <div className="flex gap-6 p-3 rounded-lg bg-slate-50 border border-slate-200 text-sm">
                <div><div className="text-xl font-bold">{validation.items}</div><div className="text-xs text-slate-500">položek</div></div>
                <div><div className="text-xl font-bold text-red-600">{validation.errors}</div><div className="text-xs text-slate-500">chyb</div></div>
                <div><div className="text-xl font-bold text-amber-600">{validation.warnings}</div><div className="text-xs text-slate-500">varování</div></div>
              </div>
              {validation.issues.length === 0 ? (
                <div className="flex items-center gap-2 text-emerald-700 text-sm"><CheckCircle className="w-4 h-4" /> Bez nálezů</div>
              ) : validation.issues.map(issue => {
                const sev = SEVERITY[issue.severity];
                const Icon = sev.icon;
                return (
                  <div key={issue.type} className={`rounded-lg border p-3 ${sev.className}`}>
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <Icon className="w-4 h-4" /> {issue.label}
                      <span className="ml-auto">{issue.count}×</span>
                    </div>
                    <div className="text-xs mt-1 opacity-80">Např.: {issue.samples.join(', ')}</div>
                  </div>
                );
              })}
              {validation.issues.some(i => i.type === 'apparel_gender') && (
                <p className="text-xs text-slate-500">
                  Pohlaví doplníš jedním pravidlem omezeným na tento kanál: PAK Nastavit hodnotu, pole Pohlaví, hodnota female.
                </p>
              )}
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setValidation(null)}>Zavřít</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
